import { css, html, LitElement, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import dataOffer from './offers.js';
import assetsPath from '../../index.js';

interface Child {
  technology: string;
  range: string;
  seo: string;
  baseline: string;
}

interface OfferData {
  offer: string;
  children: Child[];
}

/**
 * Nordnet Range logo atom. Used standalone.
 *
 * @prop context : The context. If set with 'nncom' value, baseline is encapsulated in h3 instead of div.
 * @prop alternate : Change color of Logo and text to white if value is 'true'.
 * @prop offer : The offer. (i.e. FIBRE_VITAL)
 */
@customElement('nn-range-logo')
export class RangeLogo extends LitElement {
  static styles = css`
    .range-logo {
      padding: var(--range-logo-padding, 0 4px);
    }
    .range-logo .subtitle-03 {
      color: var(--nn-grey-3, #494949);
      font-family: 'font-heavy';
      font-size: var(--range-logo-subtitle-03-font-size, 16px);
      font-weight: normal;
      line-height: calc(2ex + 4px);
      margin: 0;
      padding: 0;
      position: relative;
    }
    .range-logo.alternate .subtitle-03 {
      color: var(--nn-white, #fff);
    }
    .range-logo .sr-only {
      border: 0;
      clip: rect(0, 0, 0, 0);
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      white-space: nowrap;
      width: 1px;
      position: absolute;
    }
    .range-logo.align {
      text-align: center;
    }
    .range-logo .img-responsive {
      display: inline-block;
      height: 32px;
      max-width: 100%;
    }
  `;

  @property({ type: String }) offer = '';

  @property({ type: Boolean }) alternate = false;

  @property({ type: Boolean }) align = false;

  @property({ type: String }) context = '';

  render() {
    const itemTemplates: TemplateResult[] = [];
    const offers: OfferData[] = JSON.parse(
      JSON.stringify(dataOffer).replace(/<prefixPath>/g, assetsPath)
    );
    const selectedOffer = offers.find(
      (offer: OfferData) => offer.offer === this.offer
    );
    selectedOffer?.children.forEach((child: Child) => {
      itemTemplates.push(html`
        <div
          class="range-logo${this.alternate ? ` alternate` : ``}${this.align
            ? ` align`
            : ``}"
        >
          <img
            class="img-responsive"
            src="${assetsPath}/images/logos/offer/${child.range}${this.alternate
              ? `-white`
              : ``}.svg"
            loading="lazy"
            onerror="this.style.display='none'"
            aria-hidden="true"
            alt=""
            height="32"
          />
          ${this.context === 'nncom'
            ? html`
                <h3 class="subtitle-03">
                  <span class="sr-only">${child.seo}</span>${child.baseline}
                </h3>
              `
            : html`
                <div class="subtitle-03">
                  <span class="sr-only">${child.seo}</span>${child.baseline}
                </div>
              `}
        </div>
      `);
    });
    return html`${itemTemplates}`;
  }
}
